import React from 'react';
import { CellLoader, GetColor, Icon, Label, Tooltip, TooltipPosition } from 'venn-ui-kit';
import styled from 'styled-components';
import type { AnalysisSubject, ComparisonSubject } from 'venn-utils';
import useSubjectAvailableRange from '../logic/useSubjectAvailableRange';

interface SubjectHeaderProps {
  subject: ComparisonSubject;
  onRemove: () => void;
  onHover: (hover: boolean) => void;
}

const NAME_MAX_CHARS = 30; // Seems to be about the length of two lines.

const formatName = (name: string) => {
  if (name.length < NAME_MAX_CHARS) {
    return name;
  }
  return name.slice(0, NAME_MAX_CHARS).concat('...');
};

const getIconType = (analysisSubject: AnalysisSubject) => {
  if (analysisSubject.portfolio) {
    return 'folder';
  }
  return analysisSubject.isCompositeBenchmark ? 'layer-group' : 'square';
};

const SubjectHeader: React.FC<React.PropsWithChildren<SubjectHeaderProps>> = ({ subject, onRemove, onHover }) => {
  const { formattedRange, rangeLoading } = useSubjectAvailableRange(subject.analysisSubject);

  if (!subject.analysisSubject) {
    return (
      <div className="h-[72px] pt-0.5 pb-3 px-0.5">
        <div className="loading-placeholder bg-venn-grey-10 h-full w-[90%] my-0 mx-auto" />
      </div>
    );
  }
  return (
    <Tooltip
      position={TooltipPosition.Top}
      block
      content={
        <Label inverted>
          {subject.isBenchmark
            ? 'Benchmark'
            : subject.analysisSubject.isCompositeBenchmark
              ? 'Composite Benchmark'
              : subject.analysisSubject.type === 'investment'
                ? 'Investment'
                : 'Portfolio'}
          : {subject.analysisSubject.name}
        </Label>
      }
    >
      <Wrapper
        color={subject.color}
        hovered={subject.isHovered}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
      >
        <ColoredBar color={subject.color} />
        <Row>
          <ContentGutter />
          <NameWrapper>
            <Icon type={getIconType(subject.analysisSubject)} /> {formatName(subject.analysisSubject.name)}
          </NameWrapper>
          <ContentGutter>
            {subject.isHovered && (
              <button type="button" onClick={onRemove}>
                <Icon type="times" />
              </button>
            )}
          </ContentGutter>
        </Row>
        <div>{rangeLoading ? <CellLoader /> : <span className="text-venn-grey-90">{formattedRange}</span>}</div>
      </Wrapper>
    </Tooltip>
  );
};

const Wrapper = styled.div<{ hovered: boolean; color: string }>`
  background-color: ${(props) => (props.hovered ? GetColor.GreyScale.Grey10 : 'initial')};
  color: ${(props) => props.color || GetColor.Black};
  font-size: 12px;
  text-align: center;
  height: 72px;
  padding: 2px 2px 12px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  height: 42px;
`;

const ColoredBar = styled.div<{ color: string }>`
  background-color: ${(props) => props.color || GetColor.Black};
  margin-bottom: 7px;
  height: 5px;
  width: 100%;
`;

const ContentGutter = styled.div`
  min-width: 10%;
  flex: auto;
`;

const NameWrapper = styled.div`
  min-width: 80%;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default SubjectHeader;
